import { forwardRef } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/utils';

import Spinner from './Spinner';

const buttonVariants = cva(
	'relative inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
	{
		variants: {
			variant: {
				default:
					'bg-black text-white hover:bg-[#404040] active:bg-[#6A6A6A] disabled:bg-grey-200 disabled:text-grey-300',
				outline:
					'border border-grey-200 bg-transparent text-black hover:border-black hover:bg-grey-100 active:bg-white disabled:border-grey-200 disabled:text-grey-300',
				secondary:
					'bg-white text-black hover:bg-white/80 disabled:bg-grey-200 disabled:text-grey-300',
				outlineWhite:
					'border border-white bg-transparent text-white hover:bg-white/10 active:bg-white/20 disabled:border-grey-200 disabled:text-grey-300',
				ghost: 'hover:bg-grey-100 active:bg-grey-200 disabled:text-grey-300'
			},
			hasIcon: {
				true: 'gap-2'
			},
			size: {
				default: 'h-10 px-6 py-2',
				sm: 'h-7 px-3',
				lg: 'h-12 rounded-lg px-6 py-3',
				xs: 'h-6 px-3 text-xs font-semibold leading-5',
				icon: 'h-10 w-10',
				iconMd: 'h-8 w-8',
				iconSm: 'h-6 w-6',
				iconXs: 'h-[18px] w-[18px]'
			},
			hasStartIcon: { true: 'pl-4' },
			hasEndIcon: { true: 'pr-4' }
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		},
		compoundVariants: [
			{
				size: 'sm',
				hasStartIcon: true,
				class: 'pl-2 pr-3'
			},
			{
				size: 'sm',
				hasEndIcon: true,
				class: 'pl-3 pr-2'
			},
			{
				size: 'xs',
				hasStartIcon: true,
				class: 'pl-2 pr-3'
			},
			{
				size: ['default', 'lg'],
				hasStartIcon: true,
				class: 'pl-4'
			},
			{
				size: ['default', 'lg'],
				hasEndIcon: true,
				class: 'pr-4'
			}
		]
	}
);
const Button = forwardRef(
	(
		{
			className,
			loading,
			disabled,
			variant,
			size,
			asChild = false,
			children,
			Component,
			startIcon,
			endIcon,
			type = 'button',
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : 'button';
		Component ||= Comp;

		return (
			<Component
				className={cn(
					buttonVariants({
						variant,
						size,
						hasIcon: !!startIcon || !!endIcon,
						hasStartIcon: !!startIcon,
						hasEndIcon: !!endIcon,
						className
					})
				)}
				ref={ref}
				disabled={disabled || loading}
				type={type}
				{...props}
			>
				{asChild ? (
					children
				) : (
					<>
						{startIcon}
						{children}
						{endIcon}
						{loading && (
							<Spinner
								className={cn(
									'absolute inset-0 m-auto',
									variant === 'outlineWhite'
										? 'text-white'
										: 'text-black'
								)}
								size={24}
							/>
						)}
					</>
				)}
			</Component>
		);
	}
);
Button.displayName = 'Button';

export { buttonVariants };

export default Button;
